'use client';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import clsx from 'clsx';
import { useSetAtom } from 'jotai';

import { ReactComponent as Logo } from '@quick/assets/logos/quick.svg';
import { useClearCredentials } from '@quick/auth';
import { Button } from '@quick/components/Button';
import { DarkModeSwitcher } from '@quick/components/DarkModeSwitcher';

import { useAppTranslation } from '@/app/i18n/client';
import { LanguageSwitcher } from '@/app/i18n/switcher';
import { ReactComponent as PWA } from '@/assets/install_pwa.svg';
import { ReactComponent as Arrow } from '@/assets/profile-arrow.svg';
import { useLangProvider } from '@/components/core/AppLayout';
import { Image } from '@/components/core/Image';
import { NoSSR } from '@/components/core/NoSSR';
import { envs } from '@/layers/core/data/const';
import { useCountry } from '@/layers/core/deal/services/CountryContext';
import { useUser } from '@/layers/core/deal/services/useUser';
import { useUserInfo } from '@/layers/core/deal/services/useUserInfo';
import { useClickOutside } from '@/layers/platform/deal/services/useClickOutside';
import styles from '@/styles/utils.module.css';

import { loginAtom } from './atoms';
import { CountrySelector } from './CountrySelector';

const Header = () => {
  const lang = useLangProvider();
  const setLogin = useSetAtom(loginAtom);
  const handleLoginClick = () => setLogin(true);
  const { token } = useUser();
  const clearToken = useClearCredentials();

  const { t } = useAppTranslation(['common', 'dl']);

  const [menuVisible, setMenuVisible] = useState(false);
  const toggleMenu = useCallback(
    () => setMenuVisible((state) => !state),
    [setMenuVisible]
  );
  const [burger, setBurger] = useState(false);
  const toggleBurger = useCallback(
    () => setBurger((state) => !state),
    [setBurger]
  );
  const menuRef = useClickOutside<HTMLDivElement>(() => setBurger(false));
  const router = useRouter();

  const { contact } = useCountry();

  const [installPrompt, setInstallPrompt] = useState<
    Event & { prompt?: () => void; userChoice?: Promise<{ outcome: string }> }
  >();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const listener = (e: Event) => {
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', listener);
    return () => window.removeEventListener('beforeinstallprompt', listener);
  }, []);

  const goHome = useCallback(() => router.push(`/${lang}`), [router, lang]);
  const logout = useCallback(() => {
    clearToken();
    goHome();
  }, [clearToken, goHome]);
  const { data: userData } = useUserInfo();

  const userName = useMemo(
    () => userData && `${userData.first_name} ${userData.last_name}`,
    [userData]
  );

  return (
    <div
      className={clsx(
        `animate-in slide-in-from-top dark relative z-20 col-span-full flex min-h-[4rem]
        w-full justify-between bg-black shadow-xl`,
        styles.header_padding
      )}
    >
      <div className="flex shrink-0 items-center gap-4">
        <button onClick={goHome} aria-label="Home">
          <Logo height={48} />
        </button>
        <NoSSR>
          <CountrySelector className="max-sm:hidden sm:flex" />
          <DarkModeSwitcher
            className="text-2xl"
            style={{
              // ['--bg-dark' as 'width']: 'transparent',
              ['--bg-light' as 'width']: '#FDD402',
              ['--circle-dark' as 'height']: '#595959',
              ['--circle-light' as 'background']: '#fff',
              ['--color-dark' as 'border']: 'white',
              ['--color-light' as 'lightingColor']: 'black',
              ['--border-dark' as 'color']: 'white',
              ['--border-light' as 'flex']: 'white',
            }}
          />
        </NoSSR>
        <Suspense>
          <LanguageSwitcher />
        </Suspense>
      </div>
      <div className="flex min-w-0 shrink items-center gap-4">
        <NoSSR>
          {installPrompt && (
            <Button
              onClick={async () => {
                installPrompt?.prompt?.();
                const result = await installPrompt?.userChoice;
                if (result?.outcome === 'accepted') {
                  setInstallPrompt(undefined);
                }
              }}
            >
              <PWA className="h-6 w-6" />
            </Button>
          )}
          {!token && (
            <Link
              href={`/${lang}/platform`}
              className="relative shrink-0 pr-2 text-center font-semibold text-gray-600 sm:hidden
                dark:text-gray-400"
            >
              {t('dl:header.platform')}
              <span className="absolute right-0 top-0 flex h-3 w-3">
                <span
                  className="bg-brand-dark absolute inline-flex h-full w-full animate-ping rounded-full
                    opacity-75"
                ></span>
                <span className="bg-brand relative inline-flex h-3 w-3 rounded-full"></span>
              </span>
            </Link>
          )}
          <button
            className={clsx(
              `menu-button-container visible relative h-11 w-11 min-w-[2.75rem] transition
              sm:hidden`,
              burger && 'active'
            )}
            onClick={toggleBurger}
          >
            <span className="menu-button"></span>
          </button>
          {token === undefined ? (
            <div
              ref={menuRef}
              className={clsx(
                'z-10 items-center max-sm:flex-col sm:flex md:flex-row',
                'justify-end gap-4',
                burger
                  ? `max-sm:absolute max-sm:right-0 max-sm:top-full max-sm:z-20 max-sm:flex
                    max-sm:border-2 max-sm:border-gray-300 max-sm:bg-white max-sm:p-4
                    dark:max-sm:border-gray-500 dark:max-sm:bg-gray-700`
                  : 'max-sm:hidden'
              )}
              key="unlogged"
            >
              <Link href={`/${lang}/account/register-user`}>
                <Button variant="approve" kind="primary">
                  {t('dl:header.register')}
                </Button>
              </Link>
              <Button variant="black" kind="primary" onClick={handleLoginClick}>
                {t('dl:header.login')}
              </Button>
              <CountrySelector className="sm:hidden" />
            </div>
          ) : (
            <div className="flex min-w-0 items-center justify-end" key="logged">
              <div
                className={clsx(
                  'shrink-0 items-stretch max-sm:flex-col sm:contents md:items-center',
                  'justify-end gap-4',
                  burger
                    ? `max-sm:absolute max-sm:right-0 max-sm:top-full max-sm:z-20 max-sm:flex
                      max-sm:border-2 max-sm:border-gray-300 max-sm:bg-white max-sm:p-4
                      dark:max-sm:bg-gray-700`
                    : 'max-sm:hidden'
                )}
              >
                <Link
                  href={`/${lang}/platform`}
                  className="relative shrink-0 pr-2 text-center font-semibold text-gray-600 sm:hidden
                    dark:text-gray-400"
                >
                  {t('dl:header.platform')}
                  <span className="absolute right-0 top-0 flex h-3 w-3">
                    <span
                      className="bg-brand-dark absolute inline-flex h-full w-full animate-ping rounded-full
                        opacity-75"
                    ></span>
                    <span className="bg-brand relative inline-flex h-3 w-3 rounded-full"></span>
                  </span>
                </Link>
                <Link
                  href={`/${lang}/platform/services`}
                  className="mx-2 shrink-0 text-center font-semibold text-gray-600 sm:hidden
                    dark:text-gray-400"
                >
                  {t('dl:header.services')}
                </Link>
                <Link
                  href={`/${lang}/platform/payments`}
                  className="mx-2 shrink-0 text-center font-semibold text-gray-600 sm:hidden
                    dark:text-gray-400"
                >
                  {t('dl:header.payments')}
                </Link>
                <a
                  className="mx-2 shrink-0 text-center font-semibold text-gray-600 dark:text-gray-400"
                  href={contact}
                >
                  {t('dl:header.contact')}
                </a>
                <CountrySelector className="sm:hidden" />
              </div>
              <div className="flex h-10 w-auto min-w-0 shrink">
                {/** Button and name */}
                {userName && (
                  <span
                    className="block min-w-0 shrink truncate p-2 font-semibold capitalize text-gray-300
                      dark:text-white"
                    title={userName}
                  >
                    {userName}
                  </span>
                )}
                <span className="relative float-right block shrink-0">
                  <button
                    className="group relative flex justify-center"
                    onClick={toggleMenu}
                  >
                    <Image
                      src={
                        userData?.file_image
                          ? `${envs.S3}/${userData?.file_image}`
                          : undefined
                      }
                      className="circle h-10 w-10 object-cover"
                      alt="profile-img"
                      width={40}
                      height={40}
                    />
                    <span
                      className="bg-brand group-hover:bg-brand-dark border-contrast-light absolute bottom-0
                        right-0 flex h-4 w-4 items-center justify-center rounded-full border text-xs"
                    >
                      <Arrow
                        className={clsx(
                          'w-2 transform transition-all',
                          menuVisible && 'rotate-180'
                        )}
                      />
                    </span>
                  </button>
                  {menuVisible && (
                    <ul
                      className="bg-background-light border-contrast-light absolute -bottom-1 right-0.5 z-20 flex
                        translate-y-full transform list-none flex-col rounded border dark:bg-gray-800
                        dark:hover:bg-gray-600"
                    >
                      <li>
                        <button
                          className="p-2 dark:text-white"
                          onClick={logout}
                        >
                          {t('dl:header.logout')}
                        </button>
                      </li>
                    </ul>
                  )}
                </span>
              </div>
            </div>
          )}
        </NoSSR>
      </div>
    </div>
  );
};

export { Header };
